/* import __COLOCATED_TEMPLATE__ from './ftw-bin.hbs'; */
import Component from '@glimmer/component';
import Owner from '@ember/owner';
import { padLeft } from '../utils';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type CircuitService from '../services/circuit';
import type { ProductRow } from '../services/state';

export class BinaryCluster {
  @tracked value: string;
  maxLength: number;
  isMSB: boolean;
  isLSB: boolean;
  constructor(value: string, maxLength: number, isMSB: boolean, isLSB: boolean) {
    this.value = value;
    this.maxLength = maxLength;
    this.isMSB = isMSB;
    this.isLSB = isLSB;
  }
}

interface FTWBinaryPanelArgs {
  label: string;
  isExceededFtw?: boolean;
  changeButtonSelector: string;
  ftwChanged: (value: number) => void;
}

interface FTWBinaryPanelSignature {
  Args: FTWBinaryPanelArgs;
}

export default class FTWBinaryPanel extends Component<FTWBinaryPanelSignature> {
  @service declare intl: IntlService;
  @service declare circuit: CircuitService;

  @tracked isChanged = false;
  @tracked ftw;
  constructor(owner: Owner, args: FTWBinaryPanelArgs) {
    super(owner, args);
    this.ftw =
      isNaN(this.circuit.ftw) || !this.circuit.partData
        ? ''
        : padLeft(this.circuit.ftwBinary, this.circuit.partData.tuningWordWidth, '0');
    if (this.args.changeButtonSelector) {
      document.querySelector(this.args.changeButtonSelector)?.addEventListener('click', this.updateFTW);
    }
  }
  get ftwDisplay() {
    return this.isChanged && this.circuit.partData
      ? padLeft(this.ftw, this.circuit.partData.tuningWordWidth, '0')
      : !isNaN(this.circuit.ftw) && this.circuit.partData
        ? padLeft(this.circuit.ftwBinary, this.circuit.partData.tuningWordWidth, '0')
        : '';
  }
  get ftwParts() {
    const result = [];
    if (!this.isChanged && (this.circuit.hasCriticalError || this.circuit.isExceededFtw)) {
      const clusterLengths = this.clusterLengths;
      for (let i = 0; i < clusterLengths.length; i++) {
        result.push(new BinaryCluster('', clusterLengths[i] as number, false, false));
      }
    } else {
      const ftwDisplay = this.ftwDisplay;
      const clusterLengths = this.clusterLengths;
      let currentPosition = 0;
      for (let i = 0; i < clusterLengths.length; i++) {
        const clusterLen = clusterLengths[i] as number;
        const subString = ftwDisplay.substring(currentPosition, currentPosition + clusterLen);
        currentPosition += clusterLen;
        result.push(new BinaryCluster(subString, clusterLen, i === 0, i === clusterLengths.length - 1));
      }
    }
    return result;
  }
  get clusterLengths() {
    const maxBits = (this.circuit.partData as ProductRow).tuningWordWidth;
    const eightBitCount = Math.trunc(maxBits / 8);
    const remainingBit = maxBits % 8;
    const result = [];
    if (remainingBit > 0) {
      result.push(remainingBit);
    }

    for (let i = 0; i < eightBitCount; i++) {
      result.push(8);
    }
    return result;
  }
  @action setFTW() {
    const part = this.circuit.partData as ProductRow;
    this.isChanged = false;
    this.ftw = isNaN(this.circuit.ftw) ? '' : padLeft(this.circuit.ftwBinary, part.tuningWordWidth, '0');
  }
  @action updateFTW() {
    this.isChanged = false;
    if (this.args.changeButtonSelector) {
      const button = document.querySelector(this.args.changeButtonSelector) as HTMLButtonElement;
      if (button) {
        button.style.display = 'none';
      }
    }
    this.args.ftwChanged(parseInt(this.ftw, 2));
  }
  @action onInputUpdated(itemIndex: number, value: string) {
    this.ftw = this.ftwParts
      .map((item, index) => {
        if (index === itemIndex && value !== padLeft(item.value, item.maxLength, '0')) {
          return value;
        } else {
          return padLeft(item.value, item.maxLength, '0');
        }
      })
      .join('');
  }
  @action onInputChanging(/*event*/) {
    if (this.args.changeButtonSelector) {
      const button = document.querySelector(this.args.changeButtonSelector) as HTMLButtonElement;
      if (button) {
        button.style.display = 'block';
      }
    }
    this.isChanged = true;
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    FtwBin: typeof FTWBinaryPanel;
  }
}
